import { useEffect } from "react";

import classNames from "classnames/bind";
import Button from "common/blocks/Button";
import useModalContext from "common/providers/modal/useModalContext";
import usePartExchangeQuestionsContext from "features/vehicleDetails/providers/partExchangeQuestions/usePartExchangeQuestionsContext";
import { useForm } from "react-hook-form";
import { PartExchangeQuestions } from "services/motorpoint/partExchangeQuestions";

import { vehicleQuestionsModal } from "..";
import VehicleBadge from "../../components/FinanceCalculator/VehicleBadge";
import Wrapper from "../common/Wrapper";
import DropdownWithDescription from "./DropdownWithDescription";
import styles from "./VehicleConditionForm.module.scss";

const cx = classNames.bind(styles);

const formSelectPopulationHelper = (
    responses: PartExchangeQuestions.Response[] | undefined,
    descriptionObject: Record<string, string>
) => {
    if (!responses) return undefined;
    return (
        responses?.map((response) => {
            descriptionObject[response.Identifier] = response.Description;

            return {
                label: response.Title,
                value: response.Identifier,
            };
        }) ?? []
    );
};

const VehicleConditionForm = () => {
    const {
        partExQuestions,
        updateFormAnswers,
        formAnswers,
        partExQuestionsVehicle: vehicleCapData,
    } = usePartExchangeQuestionsContext();
    const { useSidebar } = useModalContext();
    const { goBack, openSidebar } = useSidebar();

    const { formState, watch, register, handleSubmit, control, setValue } = useForm();
    const { errors } = formState;

    // Use effect to 'touch' fields on load
    useEffect(() => {
        if (!formState.isDirty) {
            const keynames = Object.keys(control._fields);

            keynames.forEach((key) => {
                const values = control._formValues;
                setValue(key, values[key]);
            });
        }
    }, [control, formState.isDirty, setValue]);

    const hiddenQuestionIdentifiers = ["reason_for_valuation"];
    const dropDowns =
        partExQuestions?.PartExchangeQuestionViewModels?.filter(
            (question) => question.Type === "MultipleChoiceQuestionViewModel"
        ).map((question) => {
            const label = question.Title;
            const description: Record<string, string> = {};
            const options = formSelectPopulationHelper(question.Responses, description);
            const value = watch(question.Identifier);
            if (formAnswers?.UserAnswers?.reason_for_valuation)
                // if the reason question exists, pre-populate the reason as px
                formAnswers.UserAnswers.reason_for_valuation = "part_exchange";

            const defaultValue =
                (formAnswers?.UserAnswers as unknown as Record<string, string>)?.[
                    question.Identifier
                ] ?? "";

            if (options?.length) {
                options.push({
                    label: "Please choose...",
                    value: "",
                });
            }

            return !!options?.length ? (
                <DropdownWithDescription
                    key={question.Identifier}
                    options={options}
                    label={label}
                    isRequired
                    {...register(question.Identifier, {
                        required: {
                            message: "Please make a selection",
                            value: true,
                        },
                    })}
                    defaultValue={defaultValue}
                    hasError={!!errors?.[question.Identifier]?.message}
                    errorText={(errors?.[question.Identifier]?.message as string) ?? ""}
                    description={value && description[value]}
                    isHidden={hiddenQuestionIdentifiers.includes(question.Identifier)}
                />
            ) : null;
        }) ?? [];

    const onSubmit = (data: unknown) => {
        updateFormAnswers("UserAnswers", data);
        openSidebar(vehicleQuestionsModal);
    };

    return (
        <Wrapper>
            <div className={cx("vehicleConditionWrapper")}>
                <VehicleBadge
                    imageUrl={vehicleCapData?.ValuationImageUri ?? ""}
                    info={`${vehicleCapData?.Make} ${vehicleCapData?.Range} ${vehicleCapData?.Trim} ${vehicleCapData?.Colour}`}
                    changeVehicle={goBack}
                />
                <form className={cx("vehicleCondition")} onSubmit={handleSubmit(onSubmit)}>
                    {dropDowns}
                    <Button theme="primary" type="submit" fullWidth ariaLabel="Confirm">
                        Confirm
                    </Button>
                </form>
            </div>
        </Wrapper>
    );
};

export default VehicleConditionForm;
